<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import DRBtn from "../UI/DRBtn.vue";
import { useScreen } from "@/composable/useResponsive";

const { t } = useTranslation();

const props = withDefaults(
  defineProps<{
    message?: string;
    modelValue: boolean;
    title?: string;
    danger?: boolean;
    /** Surcharge le style du bouton de confirmation (ex. "ia" pour l'IA). */
    confirmVariant?: "primary" | "danger" | "ia";
    confirmLabel?: string;
    loading?: boolean;
    /** Bloque la confirmation tant que le contenu du slot n'est pas complet. */
    confirmDisabled?: boolean;
  }>(),
  {
    modelValue: false,
    danger: false,
    confirmDisabled: false,
  }
);

const resolvedConfirmVariant = computed(
  () => props.confirmVariant ?? (props.danger ? "danger" : "primary")
);

const resolvedTitle = computed(() => props.title ?? t("confirmation"));
const resolvedMessage = computed(
  () => props.message ?? t("confirmationMessage")
);
const resolvedConfirmLabel = computed(() => props.confirmLabel ?? t("confirm"));
const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm"): void;
}>();

const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const closeModal = () => {
  showModal.value = false;
};

const confirm = () => {
  emit("confirm");
};

const { screen } = useScreen();
</script>

<template>
  <el-dialog
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    v-model="showModal"
    :title="resolvedTitle"
    :width="screen.gt.sm ? '500px' : '100%'"
    append-to-body
    align-center
  >
    <div>
      <p>{{ resolvedMessage }}</p>
      <!-- Complément à trancher avant de confirmer (ex. un choix à faire) -->
      <slot></slot>
    </div>

    <template #footer>
      <div class="dialog-footer d-flex justify-content-end gap-2">
        <DRBtn
          :label="$t('cancel')"
          variant="outline"
          @click="closeModal"
        ></DRBtn>
        <DRBtn
          :variant="resolvedConfirmVariant"
          :label="resolvedConfirmLabel"
          @click="confirm"
          :loading="loading"
          :disabled="confirmDisabled"
        ></DRBtn>
      </div>
    </template>
  </el-dialog>
</template>

<style></style>
